import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TransactionCommentsAPI, MentionableUser } from '../services/api';
import { TransactionComment } from '../types/payment';
import { useAuth } from '../context/AuthContext';

interface TransactionCommentsProps {
  recordId: string;  // Can be transaction ID or pending_sale ID
  scope?: string;    // 'general' (default) or 'processing' (sales-hidden thread)
}

export const TransactionComments: React.FC<TransactionCommentsProps> = ({ recordId, scope = 'general' }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<TransactionComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [replyingTo, setReplyingTo] = useState<TransactionComment | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mention states
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionUsers, setMentionUsers] = useState<MentionableUser[]>([]);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mentionStartPos, setMentionStartPos] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (recordId) {
      loadComments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordId, scope]);

  // Fetch mentionable users when search changes
  useEffect(() => {
    if (showMentionDropdown) {
      const fetchUsers = async () => {
        try {
          const users = await TransactionCommentsAPI.getMentionableUsers(mentionSearch);
          setMentionUsers(users);
          setMentionIndex(0);
        } catch (err) {
          console.error('Failed to fetch mentionable users:', err);
        }
      };
      fetchUsers();
    }
  }, [showMentionDropdown, mentionSearch]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowMentionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadComments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await TransactionCommentsAPI.getComments(recordId, scope);
      setComments(response.comments);
    } catch (err: any) {
      console.error('Failed to load comments:', err);
      setError(err.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursorPos = e.target.selectionStart;
    setNewComment(value);

    // Check if user is typing a mention
    const textBeforeCursor = value.slice(0, cursorPos);
    const atIndex = textBeforeCursor.lastIndexOf('@');

    if (atIndex !== -1) {
      const textAfterAt = textBeforeCursor.slice(atIndex + 1);
      // Only show dropdown if @ is at start or after whitespace, and no space after @
      const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
      if ((charBeforeAt === ' ' || charBeforeAt === '\n' || atIndex === 0) && !/\s/.test(textAfterAt)) {
        setMentionStartPos(atIndex);
        setMentionSearch(textAfterAt);
        setShowMentionDropdown(true);
        return;
      }
    }
    setShowMentionDropdown(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!showMentionDropdown || mentionUsers.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMentionIndex((prev) => (prev + 1) % mentionUsers.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMentionIndex((prev) => (prev - 1 + mentionUsers.length) % mentionUsers.length);
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      insertMention(mentionUsers[mentionIndex]);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
    }
  };

  const insertMention = (mentionUser: MentionableUser) => {
    const beforeMention = newComment.slice(0, mentionStartPos);
    const afterMention = newComment.slice(mentionStartPos + mentionSearch.length + 1);
    const newValue = `${beforeMention}@${mentionUser.username} ${afterMention}`;
    setNewComment(newValue);
    setShowMentionDropdown(false);

    // Focus textarea and set cursor position after mention
    setTimeout(() => {
      if (textareaRef.current) {
        const cursorPos = mentionStartPos + mentionUser.username.length + 2;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(cursorPos, cursorPos);
      }
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      setError(null);
      await TransactionCommentsAPI.createComment(
        recordId,
        newComment.trim(),
        replyingTo?.id,
        scope
      );
      setNewComment('');
      setReplyingTo(null);
      await loadComments();
    } catch (err: any) {
      setError(err.message || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      setError(null);
      await TransactionCommentsAPI.deleteComment(recordId, commentId);
      await loadComments();
    } catch (err: any) {
      setError(err.message || 'Failed to delete comment');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getRoleBadgeColor = (role?: string) => {
    switch (role) {
      case 'admin': return 'role-admin';
      case 'director': return 'role-director';
      case 'floor_manager': return 'role-manager';
      case 'sales_team_lead':
      case 'processor_team_lead': return 'role-lead';
      default: return 'role-agent';
    }
  };

  const formatRoleName = (role?: string) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Format comment content with highlighted mentions
  const formatCommentContent = (content: string) => {
    const mentionRegex = /@([a-zA-Z0-9_.\-]+)/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Add text before mention
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      // Add highlighted mention
      parts.push(
        <span key={match.index} className="mention-highlight">
          @{match[1]}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }
    // Add remaining text
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  // Separate top-level comments and replies
  const topLevelComments = comments.filter(c => !c.parent_comment_id);
  const getReplies = (commentId: string) => comments.filter(c => c.parent_comment_id === commentId);

  return (
    <div className="transaction-comments">
      <h3>Comments ({comments.length})</h3>

      {error && (
        <div className="comment-error">
          {error}
        </div>
      )}

      {/* New Comment Form */}
      <form onSubmit={handleSubmit} className="comment-form">
        {replyingTo && (
          <div className="replying-to">
            <span>Replying to {replyingTo.author_name}...</span>
            <button type="button" className="btn-link" onClick={() => setReplyingTo(null)}>
              Cancel
            </button>
          </div>
        )}
        <div className="textarea-container">
          <textarea
            ref={textareaRef}
            value={newComment}
            onChange={handleTextChange}
            onKeyDown={handleKeyDown}
            placeholder={replyingTo ? "Write a reply... (type @ to mention)" : "Add a comment... (type @ to mention someone)"}
            rows={3}
            disabled={submitting}
          />
          {showMentionDropdown && mentionUsers.length > 0 && (
            <div ref={dropdownRef} className="mention-dropdown">
              {mentionUsers.map((mentionUser, index) => (
                <div
                  key={mentionUser.id}
                  className={`mention-item ${index === mentionIndex ? 'selected' : ''}`}
                  onClick={() => insertMention(mentionUser)}
                  onMouseEnter={() => setMentionIndex(index)}
                >
                  <span className="mention-name">{mentionUser.full_name}</span>
                  <span className="mention-username">@{mentionUser.username}</span>
                  {mentionUser.role && (
                    <span className={`mention-role ${getRoleBadgeColor(mentionUser.role)}`}>
                      {formatRoleName(mentionUser.role)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
          disabled={!newComment.trim() || submitting}
        >
          {submitting ? 'Posting...' : (replyingTo ? 'Post Reply' : 'Post Comment')}
        </button>
      </form>

      {/* Comments List */}
      {loading ? (
        <div className="comments-loading">Loading comments...</div>
      ) : comments.length === 0 ? (
        <div className="no-comments">
          No comments yet. Be the first to add one.
        </div>
      ) : (
        <div className="comments-list">
          {topLevelComments.map((comment) => (
            <div key={comment.id} className="comment-thread">
              {/* Top-level comment */}
              <div className={`comment ${comment.is_deleted ? 'deleted' : ''}`}>
                <div className="comment-header">
                  <div className="comment-author">
                    <span className="author-name">{comment.author_name}</span>
                    {comment.author_role && (
                      <span className={`role-badge ${getRoleBadgeColor(comment.author_role)}`}>
                        {formatRoleName(comment.author_role)}
                      </span>
                    )}
                  </div>
                  <span className="comment-date">{formatDate(comment.created_at)}</span>
                </div>
                <div className="comment-content">
                  {comment.is_deleted ? (
                    <em className="deleted-text">[Comment deleted by admin]</em>
                  ) : (
                    formatCommentContent(comment.content)
                  )}
                </div>
                {!comment.is_deleted && (
                  <div className="comment-actions">
                    <button
                      className="btn-link"
                      onClick={() => setReplyingTo(comment)}
                    >
                      Reply
                    </button>
                    {isAdmin && (
                      <button
                        className="btn-link delete"
                        onClick={() => handleDelete(comment.id)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Replies */}
              {getReplies(comment.id).map((reply) => (
                <div key={reply.id} className={`comment reply ${reply.is_deleted ? 'deleted' : ''}`}>
                  <div className="comment-header">
                    <div className="comment-author">
                      <span className="author-name">{reply.author_name}</span>
                      {reply.author_role && (
                        <span className={`role-badge ${getRoleBadgeColor(reply.author_role)}`}>
                          {formatRoleName(reply.author_role)}
                        </span>
                      )}
                    </div>
                    <span className="comment-date">{formatDate(reply.created_at)}</span>
                  </div>
                  <div className="comment-content">
                    {reply.is_deleted ? (
                      <em className="deleted-text">[Comment deleted by admin]</em>
                    ) : (
                      formatCommentContent(reply.content)
                    )}
                  </div>
                  {!reply.is_deleted && (
                    <div className="comment-actions">
                      <button
                        className="btn-link"
                        onClick={() => setReplyingTo(reply)}
                      >
                        Reply
                      </button>
                      {isAdmin && (
                        <button
                          className="btn-link delete"
                          onClick={() => handleDelete(reply.id)}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <style>{`
        .transaction-comments {
          margin-top: 1rem;
        }

        .transaction-comments h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 600;
        }

        .comment-error {
          padding: 0.75rem;
          background: var(--danger-light);
          border: 1px solid var(--danger);
          border-radius: 4px;
          color: var(--danger);
          margin-bottom: 1rem;
          font-size: 0.875rem;
        }

        .comment-form {
          margin-bottom: 1.5rem;
        }

        .comment-form .replying-to {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: var(--primary-light);
          border-radius: 4px 4px 0 0;
          font-size: 0.875rem;
          color: var(--primary);
        }

        .textarea-container {
          position: relative;
        }

        .comment-form textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-card);
          color: var(--text-primary);
          font-family: inherit;
          font-size: 0.875rem;
          resize: vertical;
          min-height: 80px;
        }

        .comment-form textarea:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px var(--primary-light);
        }

        .comment-form button[type="submit"] {
          margin-top: 0.5rem;
        }

        /* Mention Dropdown */
        .mention-dropdown {
          position: absolute;
          bottom: 100%;
          left: 0;
          right: 0;
          max-height: 200px;
          overflow-y: auto;
          background: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          margin-bottom: 4px;
        }

        .mention-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          cursor: pointer;
          border-bottom: 1px solid var(--border-primary);
        }

        .mention-item:last-child {
          border-bottom: none;
        }

        .mention-item:hover,
        .mention-item.selected {
          background: var(--bg-hover);
        }

        .mention-name {
          font-weight: 500;
          color: var(--text-primary);
        }

        .mention-username {
          color: var(--text-secondary);
          font-size: 0.85rem;
        }

        .mention-role {
          margin-left: auto;
          padding: 0.125rem 0.5rem;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        /* Mention Highlight in Comments */
        .mention-highlight {
          color: var(--primary);
          font-weight: 500;
          background: var(--primary-light);
          padding: 0.1rem 0.25rem;
          border-radius: 3px;
        }

        .comments-loading,
        .no-comments {
          padding: 2rem;
          text-align: center;
          color: var(--text-secondary);
          font-size: 0.875rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px dashed var(--border-primary);
        }

        .comments-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .comment-thread {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .comment {
          padding: 1rem;
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
        }

        .comment.reply {
          margin-left: 2rem;
          border-left: 3px solid var(--primary);
        }

        .comment.deleted {
          opacity: 0.7;
        }

        .comment-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .comment-author {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .author-name {
          font-weight: 600;
          color: var(--text-primary);
          font-size: 0.9rem;
        }

        .role-badge {
          padding: 0.125rem 0.5rem;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .role-badge.role-admin {
          background: var(--danger-light);
          color: var(--danger);
        }

        .role-badge.role-director {
          background: var(--primary-light);
          color: var(--primary);
        }

        .role-badge.role-manager {
          background: var(--warning-light);
          color: var(--warning-dark);
        }

        .role-badge.role-lead {
          background: var(--info-light);
          color: var(--info);
        }

        .role-badge.role-agent {
          background: var(--secondary-light);
          color: var(--secondary);
        }

        .comment-date {
          color: var(--text-secondary);
          font-size: 0.75rem;
        }

        .comment-content {
          color: var(--text-primary);
          font-size: 0.9rem;
          line-height: 1.5;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        .deleted-text {
          color: var(--text-muted);
        }

        .comment-actions {
          margin-top: 0.5rem;
          display: flex;
          gap: 1rem;
        }

        .btn-link {
          background: none;
          border: none;
          color: var(--primary);
          font-size: 0.8rem;
          cursor: pointer;
          padding: 0;
          text-decoration: none;
        }

        .btn-link:hover {
          text-decoration: underline;
        }

        .btn-link.delete {
          color: var(--danger);
        }

        .btn-link.delete:hover {
          color: var(--danger-dark);
        }
      `}</style>
    </div>
  );
};
